import React, { Fragment, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { loyaltyApi } from '@app/store';
import { ILoyalty, LoyaltyDTO } from '@app/common';
import { Button, Form, Panel } from '@app/components';
import { LoyaltyForm } from './LoyaltyForm';
import {
  LoyaltyFormValues,
  transformLoyaltyFormValuesToPayload,
  transformToUpdatableFormLoyalty,
} from './loyalty-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { LoyaltyCard } from './LoyaltyCard';
import { useAppDispatch } from '@app/store/hooks';

interface LoyaltyUpdateProps {
  loyalty?: Partial<ILoyalty>;
  isFetching: boolean;
}

export const LoyaltyUpdate = ({ loyalty, isFetching }: LoyaltyUpdateProps) => {
  const toast = useToast();

  const dispatch = useAppDispatch();

  const triptych = useTriptych();

  const [upsert, { isLoading, error: apiErrors }] = loyaltyApi.useCreateOneLoyaltiesMutation();

  const formMethods = useForm<LoyaltyFormValues>({
    resolver: classValidatorResolver(LoyaltyDTO),
    defaultValues: transformToUpdatableFormLoyalty(loyalty),
  });

  const onSubmit = async (formValues: LoyaltyFormValues) => {
    try {
      const updatedLoyalty = await upsert(transformLoyaltyFormValuesToPayload(formValues)).unwrap();

      dispatch(loyaltyApi.endpoints.readOneLoyalties.initiate({}, { forceRefetch: true }));

      triptych.close({ mode: 'UPDATE', data: updatedLoyalty });

      toast.success('Votre programme de fidélité a bien été modifié.');
    } catch {
      toast.error(
        'Une erreur est survenue lors de la modification de votre programme de fidélité.',
      );
    }
  };

  useEffect(() => {
    formMethods.reset(transformToUpdatableFormLoyalty(loyalty));
  }, [loyalty, formMethods.reset]);

  return (
    <Fragment>
      <LoyaltyCard loyalty={loyalty} formMethods={formMethods} />

      <Panel.Content>
        <Form formMethods={formMethods} apiErrors={apiErrors} isLoading={isFetching}>
          <Panel.FormContainer>
            <LoyaltyForm />
          </Panel.FormContainer>

          <Panel.Actions>
            <Button title="Retour" grow large bordered onPress={() => triptych.close()} />

            <Button
              loading={isLoading}
              disabled={isFetching}
              onPress={formMethods.handleSubmit(onSubmit)}
              title="Valider"
              grow
              large
            />
          </Panel.Actions>
        </Form>
      </Panel.Content>
    </Fragment>
  );
};
